import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, MenuItem, Select, InputLabel,
  FormControl, Typography, Alert, Grid, Card, Avatar, Paper, Chip
} from '@mui/material';
import {
  TimePicker, DatePicker, LocalizationProvider, StaticTimePicker, MobileTimePicker
} from '@mui/x-date-pickers';
import LeaveCalendar from '../calendars/CalendarCard';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Person as PersonIcon, AccessTime, CalendarToday, EventNote } from '@mui/icons-material';
import { enGB } from 'date-fns/locale';
import { IMAGE_BASE_URL } from '../../config';
import { createAbsence, stageAbsence } from '../../services/absenceAPIs';
import { getMyImage } from '../../services/EmployeeService';

// WORKING DAY CONFIG
const WORK_START_HOUR = 8;
const WORK_START_MINUTE = 30;
const WORK_END_HOUR = 17;
const WORK_END_MINUTE = 30;

// BREAK GROUPS
const BREAK_GROUPS = {
  A: {           // 12:30 – 13:30
    label: '12:30 – 13:30',
    startHour: 12,
    startMinute: 30,
    endHour: 13,
    endMinute: 30,
  },
  B: {           // 13:15 – 14:15
    label: '13:15 – 14:15',
    startHour: 13,
    startMinute: 15,
    endHour: 14,
    endMinute: 15,
  },
  C: {           // 14:00 – 15:00
    label: '14:00 – 15:00',
    startHour: 14,
    startMinute: 0,
    endHour: 15,
    endMinute: 0,
  },
};

const toMinutes = (hours, minutes) => hours * 60 + minutes;

// const isDisabled = localStorage.getItem('userRole') !== 'EMPLOYEE';

const AbsenceForm = ({
  ty_pe,
  employeeId,
  onSubmitSuccess,
  fetchAbsences,
  IsEmployee,
  employees,
  Lateness = false
}) => {
  const [formData, setFormData] = useState({
    type: '',
    ty_pe: ty_pe || '',
    date: null,
    endDate: null,
    startTime: null,
    endTime: null,
    reason: '',
    hours: '',
    breakGroup: 'A', // default break group
  });

  const isDisabled = !IsEmployee;
  console.log('AbsenceForm - IsEmployee:', IsEmployee, 'Lateness:', Lateness);
  console.log('AbsenceForm - IsDisabled:', isDisabled);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [autoCalculated, setAutoCalculated] = useState(false);

  // Fetch own image if IsEmployee and no image in selectedEmployee
  useEffect(() => {
    const fetchOwnImage = async () => {
      if (IsEmployee) {
        try {
          const url = await getMyImage();
          setProfileImageUrl(url);
        } catch (err) {
          console.error('Failed to load own image:', err);
          setProfileImageUrl(null);
        }
      }
    };
    fetchOwnImage();
  }, [IsEmployee]);

  useEffect(() => {
    return () => {
      if (profileImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [profileImageUrl]);

  useEffect(() => {
    if (Lateness) {
      setFormData(prev => ({
        ...prev,
        type: 'PARTIAL_DAY',
        ty_pe: 'LATENESS',
      }));
    } else {
      if (formData.ty_pe === 'LATENESS') {
        setFormData(prev => ({
          ...prev,
          ty_pe: '',
          type: prev.type === 'PARTIAL_DAY' && prev.ty_pe === 'LATENESS' ? '' : prev.type,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Lateness]);

  useEffect(() => {
    if (
      formData.startTime &&
      formData.endTime &&
      formData.type === 'PARTIAL_DAY' &&
      formData.ty_pe !== 'LATENESS'
    ) {
      calculateHours();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startTime, formData.endTime, formData.breakGroup]);

  const calculateHours = () => {
    if (!formData.startTime || !formData.endTime) return;

    let start, end;

    // If user typed the time manually (string like "15:15")
    if (typeof formData.startTime === 'string' && formData.startTime.includes(':')) {
      const [h1, m1] = formData.startTime.split(':').map(Number);
      start = new Date();
      start.setHours(h1, m1, 0, 0);
    } else {
      start = new Date(formData.startTime);
    }

    if (typeof formData.endTime === 'string' && formData.endTime.includes(':')) {
      const [h2, m2] = formData.endTime.split(':').map(Number);
      end = new Date();
      end.setHours(h2, m2, 0, 0);
    } else {
      end = new Date(formData.endTime);
    }

    // Convert to minutes from midnight
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();

    // Working day bounds
    const workStart = toMinutes(WORK_START_HOUR, WORK_START_MINUTE); // 8:30
    const workEnd = toMinutes(WORK_END_HOUR, WORK_END_MINUTE);       // 17:30

    // Clip absence interval to working hours
    let effectiveStart = Math.max(startMinutes, workStart);
    let effectiveEnd = Math.min(endMinutes, workEnd);

    if (effectiveEnd <= effectiveStart) {
      setError('End time must be after start time and within working hours');
      setFormData(prev => ({ ...prev, hours: '' }));
      setAutoCalculated(false);
      return;
    }

    // Get break group config
    const group = BREAK_GROUPS[formData.breakGroup] || BREAK_GROUPS['A'];
    const breakStart = toMinutes(group.startHour, group.startMinute);
    const breakEnd = toMinutes(group.endHour, group.endMinute);

    // Overlap between absence and break
    const overlapStart = Math.max(effectiveStart, breakStart);
    const overlapEnd = Math.min(effectiveEnd, breakEnd);
    const breakOverlap = Math.max(0, overlapEnd - overlapStart);

    // Total working minutes absent (excluding break)
    const workingMinutes = (effectiveEnd - effectiveStart) - breakOverlap;

    if (workingMinutes <= 0) {
      setError('No working time absent (absence only during break or outside work).');
      setFormData(prev => ({ ...prev, hours: '' }));
      setAutoCalculated(false);
      return;
    }

    const diffHours = workingMinutes / 60;

    setFormData(prev => ({
      ...prev,
      hours: diffHours.toFixed(2),
    }));
    setAutoCalculated(true);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (Lateness && (name === 'type' || name === 'ty_pe')) return;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (!Lateness && name === 'ty_pe' && value === 'LATENESS') {
      setFormData(prev => ({ ...prev, type: 'PARTIAL_DAY' }));
    }

    if (!Lateness && name === 'type' && value !== 'PARTIAL_DAY' && formData.ty_pe === 'LATENESS') {
      setFormData(prev => ({ ...prev, ty_pe: '' }));
    }
  };

  const handleDateChange = (name) => (date) =>
    setFormData(prev => ({ ...prev, [name]: date }));

  const handleTimeChange = (field) => (newValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const handleCalendarDateSelect = (date1) => {
    if (!formData.date || (formData.date && formData.endDate)) {
      setFormData(prev => ({ ...prev, date: date1, endDate: null }));
    } else if (formData.date && !formData.endDate) {
      if (date1 >= formData.date) {
        setFormData(prev => ({ ...prev, endDate: date1 }));
      } else {
        setFormData(prev => ({ ...prev, date: date1, endDate: null }));
      }
    }
  };

  const selectedEmployee = employees?.find(emp => emp.employeeId === employeeId);
  const employeeName = selectedEmployee
    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : employeeId ? `Employee ID: ${employeeId}` : 'Select an employee';

  const validateForm = () => {
    const { type, ty_pe, date, endDate, reason, hours } = formData;

    if ((employeeId === null || employeeId === undefined) && !IsEmployee) {
      setError('Please select an employee.');
      return false;
    }

    if (!type || !date) {
      setError('Type, date, and reason are required');
      return false;
    }

    // Year validation - only current, previous, or next year
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();

    if (dateYear < currentYear - 1 || dateYear > currentYear + 1) {
      setError(`Invalid year. Please select a date from ${currentYear - 1}, ${currentYear}, or ${currentYear + 1}.`);
      return false;
    }

    if (type === 'MULTIPLE_DAYS' && !endDate) {
      setError('End date is required for multiple day absences');
      return false;
    }

    if (type === 'MULTIPLE_DAYS' && endDate < date) {
      setError('End date cannot be before start date');
      return false;
    }

    // Year validation for end date
    if (type === 'MULTIPLE_DAYS' && endDate) {
      const endDateYear = endDate.getFullYear();
      if (endDateYear < currentYear - 1 || endDateYear > currentYear + 1) {
        setError(`Invalid end date year. Please select a date from ${currentYear - 1}, ${currentYear}, or ${currentYear + 1}.`);
        return false;
      }
    }

    if (type === 'PARTIAL_DAY') {
      if (!hours || isNaN(hours)) {
        setError('Hours must be a valid number for partial day absences.');
        return false;
      }

      if (ty_pe === 'LATENESS') {
        const minutesValue = parseFloat(hours);
        if (isNaN(minutesValue) || minutesValue <= 0) {
          setError('Minutes late must be a number greater than 0.');
          return false;
        }
        if (minutesValue > 60) {
          setError('Lateness cannot exceed 60 minutes.');
          return false;
        }
      } else {
        const h = parseFloat(hours);
        if (h <= 0 || h > 7) {
          setError('Hours must be between 0 and 7 working hours.');
          return false;
        }
      }
    }

    return true;
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeOnly = (date) => {
    if (!date) return null;
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    let finalHours = formData.hours;
    if (formData.ty_pe === 'LATENESS' && formData.hours) {
      // Minutes (in "hours" field) -> convert to hours for backend
      const minutes = parseFloat(formData.hours);
      finalHours = (minutes / 60).toFixed(2);
    }

    const basePayload = {
      ...formData,
      hours: finalHours,
      date: formatDateOnly(formData.date),
      endDate:
        formData.type === 'FULL_DAY'
          ? formatDateOnly(formData.date)
          : formatDateOnly(formData.endDate),
      startTime: formData.startTime ? formatTimeOnly(formData.startTime) : null,
      endTime: formData.endTime ? formatTimeOnly(formData.endTime) : null,
    };

    // breakGroup is informational for now; keep it if backend expects it,
    // or remove here if backend doesn't support.
    // Example: keep as is.

    if (!basePayload.startTime) delete basePayload.startTime;
    if (!basePayload.endTime) delete basePayload.endTime;

    try {
      if (IsEmployee) {
        await stageAbsence(basePayload);
      } else {
        await createAbsence({
          ...basePayload,
          employee: { employeeId },
        });
      }

      setSuccess('Absence recorded successfully.');
      setFormData(prev => ({
        type: Lateness ? 'PARTIAL_DAY' : '',
        ty_pe: Lateness ? 'LATENESS' : (ty_pe || ''),
        date: null,
        endDate: null,
        startTime: null,
        endTime: null,
        reason: '',
        hours: '',
        breakGroup: 'A',
      }));

      if (onSubmitSuccess) {
        setTimeout(() => onSubmitSuccess(), 500);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to submit absence.'
      );
      console.error(err);
    }
  };

  const absenceTypes = [
    { value: 'PARTIAL_DAY', label: 'Partial Day' },
    { value: 'FULL_DAY', label: 'Full Day' },
    { value: 'MULTIPLE_DAYS', label: 'Multiple Days' },
  ];

  const absenceTyPes = [
    { value: 'SICKNESS', label: 'Sickness' },
    { value: 'PERSONAL', label: 'Personal Commitment' },
    { value: 'SUSPENSION', label: 'Suspension' },
    { value: 'DAY_OFF', label: 'Day Off' },
    { value: 'UNKNOWN', label: 'Unknown' }
  ];

  const employeeAbsenceTyPes = [
    { value: 'SICKNESS', label: 'Sickness' },
    { value: 'PERSONAL', label: 'Personal Commitment' },
  ];

  const filteredAbsenceTypes = Lateness
    ? [{ value: 'PARTIAL_DAY', label: 'Partial Day' }]
    : absenceTypes;

  const filteredAbsenceTyPes = Lateness
    ? [{ value: 'LATENESS', label: 'Lateness' }]
    : IsEmployee
      ? employeeAbsenceTyPes
      : absenceTyPes;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'transparent',
        p: 3,
      }}>
        <Grid container spacing={3} sx={{ width: '100%', height: '100%' }}>
          {/* Main Form Card */}
          <Grid sx={{ width: '62%' }} item>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                overflow: 'visible',
                background: 'white',
                height: '100%'
              }}
            >
              <Box sx={{
                background: 'linear-gradient(135deg, #7091e6 0%, #3d52a0 100%)',
                p: 3,
                color: 'white',
                borderRadius: 3,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      border: '3px solid rgba(255,255,255,0.3)',
                      fontWeight: 'bold',
                      fontSize: '1.3rem',
                    }}
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Me"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : selectedEmployee?.image ? (
                      <img
                        src={`${IMAGE_BASE_URL}${selectedEmployee.image}`}
                        alt="Employee"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : selectedEmployee ? (
                      `${selectedEmployee.firstName[0]}${selectedEmployee.lastName[0]}`.toUpperCase()
                    ) : (
                      <PersonIcon sx={{ fontSize: 28 }} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={700}>
                      {Lateness ? 'Record Lateness' : 'Absence Records'}
                    </Typography>
                    {employeeId && (
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        {employeeName}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    icon={<EventNote />}
                    label={Lateness ? "Lateness" : "Absence"}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                      border: '1px solid rgba(255,255,255,0.3)'
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Alerts */}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)'
                    }}
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)'
                    }}
                  >
                    {success}
                  </Alert>
                )}

                {/* Form */}
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Absence Period */}
                  {!(IsEmployee && Lateness) && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Absence Period</InputLabel>
                        <Select
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          label="Absence Period"
                          disabled={Lateness}
                          sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e0e0e0'
                            }
                          }}
                        >
                          {filteredAbsenceTypes.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Absence Type */}
                  {!(IsEmployee && Lateness) && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Absence Type</InputLabel>
                        <Select
                          name="ty_pe"
                          value={formData.ty_pe}
                          onChange={handleChange}
                          label="Absence Type"
                          disabled={isDisabled}
                          sx={{ borderRadius: 2 }}
                        >
                          {filteredAbsenceTyPes.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Start Date */}
                  <Box sx={{ mb: 3 }}>
                    <DatePicker
                      label="Start Date"
                      value={formData.date}
                      onChange={handleDateChange('date')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { borderRadius: 2 }
                        }
                      }}
                      format="dd/MM/yyyy"
                    />
                  </Box>

                  {/* End Date (only for multiple days) */}
                  {formData.type === 'MULTIPLE_DAYS' && (
                    <Box sx={{ mb: 3 }}>
                      <DatePicker
                        label="End Date"
                        value={formData.endDate}
                        onChange={handleDateChange('endDate')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { borderRadius: 2 }
                          }
                        }}
                        format="dd/MM/yyyy"
                      />
                    </Box>
                  )}

                  {/* Break Group (partial day, not lateness) */}
                  {formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS' && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Break Group</InputLabel>
                        <Select
                          name="breakGroup"
                          value={formData.breakGroup}
                          onChange={handleChange}
                          label="Break Group"
                        >
                          {Object.entries(BREAK_GROUPS).map(([key, group]) => (
                            <MenuItem key={key} value={key}>
                              {group.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Start Time */}
                  {formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS' && (
                    <Box sx={{ mb: 3 }}>
                      <MobileTimePicker
                        label="Start Time"
                        value={formData.startTime}
                        onChange={handleTimeChange('startTime')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { borderRadius: 2 }
                          }
                        }}
                        format="HH:mm"
                      />
                    </Box>
                  )}

                  {/* End Time */}
                  {formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS' && (
                    <Box sx={{ mb: 3 }}>
                      <MobileTimePicker
                        label="End Time"
                        value={formData.endTime}
                        onChange={handleTimeChange('endTime')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { borderRadius: 2 }
                          }
                        }}
                        format="HH:mm"
                      />
                    </Box>
                  )}

                  {/* Hours (Partial Day) */}
                  {formData.type === 'PARTIAL_DAY' && (
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        name="hours"
                        label={formData.ty_pe === 'LATENESS' ? 'Minutes Late' : 'Hours'}
                        type="number"
                        value={formData.hours}
                        onChange={handleChange}
                        fullWidth
                        sx={{ borderRadius: 2 }}
                        inputProps={{
                          step: formData.ty_pe === 'LATENESS' ? '1' : '0.01',
                          min: '0',
                          max: formData.ty_pe === 'LATENESS' ? '60' : '7',
                        }}
                        helperText={
                          formData.ty_pe === 'LATENESS'
                            ? 'Enter minutes late (1–60)'
                            : 'Enter hours for partial day absence (0.01–7 working hours, break excluded)'
                        }
                        InputProps={{
                          startAdornment: <AccessTime sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Box>
                  )}

                  {/* Reason */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      name="reason"
                      label="Reason"
                      value={formData.reason}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={4}
                      sx={{ borderRadius: 2 }}
                      placeholder="Please provide details about your absence..."
                    />
                  </Box>

                  {/* Submit Button */}
                  <Box>
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      size="large"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.5,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease'
                        },
                      }}
                    >
                      {Lateness ? 'Submit Lateness Record' : 'Submit Absence Request'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Calendar Card */}
          <Grid item sx={{ width: '35%' }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                minHeight: 400,
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12
              }}>
                <Typography variant="h6" fontWeight={700} color="#333">
                  <CalendarToday sx={{ verticalAlign: 'middle', mr: 1, mb: 0.5 }} />
                  Select Dates
                </Typography>
              </Box>

              <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <LeaveCalendar
                  startDate={formData.date}
                  endDate={formData.endDate}
                  onDateSelect={handleCalendarDateSelect}
                  highlightRange
                />
              </Box>

              {/* Date Summary */}
              <Paper sx={{ m: 3, mt: 0, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                  SELECTED DATES
                </Typography>
                {formData.date ? (
                  <>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>
                      From: {formData.date.toLocaleDateString('en-GB')}
                    </Typography>
                    {formData.endDate && (
                      <Typography variant="body2" fontWeight={500}>
                        To: {formData.endDate.toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    No dates selected yet
                  </Typography>
                )}
              </Paper>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default AbsenceForm;